import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError, useFetch } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame, Loader2, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { data: ministry } = useFetch("/ministry");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo variant="light" src={ministry?.logo} /></div>
        <div className="border border-white/10 bg-navy-surface p-8">
          <div className="mb-6 flex items-center gap-2 text-brand-orange">
            <Flame className="h-5 w-5" />
            <span className="font-display text-sm font-semibold uppercase tracking-[0.16em]">Admin Login</span>
          </div>
          <form onSubmit={submit} data-testid="login-form" className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm border-white/15 bg-white/5 text-white placeholder:text-white/30" placeholder="admin@…" />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative mt-2">
                <Input id="password" type={showPw ? "text" : "password"} data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-white/15 bg-white/5 pr-11 text-white placeholder:text-white/30" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-white/50 hover:text-white">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-sm bg-brand-orange py-6 font-display uppercase tracking-wide hover:bg-brand-orange-600">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
